import React, { useState, useEffect, useCallback } from 'react';
import { Target, TrendingUp, IndianRupee, CheckCircle, Clock, Package, Star, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MyPerformancePage = () => {
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [period, setPeriod] = useState('month');

  const fetchPerformance = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffSelfAPI.getMyPerformance({ period });
      setPerformance(data || {});
    } catch (error) {
      console.error('Error fetching performance:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load performance data'));
      setPerformance({});
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  // Read from nested stats object
  const stats = performance?.stats || {};

  return (
    <div className="h-full flex flex-col" data-testid="my-performance-page">
      <ListPageHeader
        title="My Performance"
        subtitle="Track your sales performance and targets"
        stats={[
          { icon: IndianRupee, label: 'Sales', value: formatCompactCurrency(stats.total_sales), color: 'emerald' },
          { icon: TrendingUp, label: 'Collections', value: formatCompactCurrency(stats.total_collections), color: 'blue' },
          { icon: CheckCircle, label: 'Visits', value: `${stats.visits_completed || 0}/${stats.visits_planned || 0}`, color: 'purple' },
          { icon: Package, label: 'Placements', value: stats.tasks_completed || 0, color: 'orange' },
          { icon: Clock, label: 'Attendance', value: `${stats.attendance_days || 0}d`, color: 'teal' },
        ]}
        secondaryAction={{
          label: period === 'month' ? 'This Month' : period === 'week' ? 'This Week' : period === 'quarter' ? 'Quarter' : 'Today',
          icon: BarChart3,
          onClick: () => {}
        }}
      />

      {/* Period Selector */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 w-fit">
            {['today', 'week', 'month', 'quarter'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                  period === p
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                }`}
              >
                {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'Quarter'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading performance data...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Performance Summary Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[14px] font-semibold text-slate-700">Performance Summary</h3>
              </div>
              <div className="p-4 space-y-4">
                {/* Sales Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[12px] text-slate-600">Sales Target</span>
                    <span className="text-[12px] font-medium text-slate-700">
                      {formatCompactCurrency(stats.total_sales)} / {formatCompactCurrency(stats.sales_target || 100000)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(((stats.total_sales || 0) / (stats.sales_target || 100000)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Visits Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[12px] text-slate-600">Visit Target</span>
                    <span className="text-[12px] font-medium text-slate-700">
                      {stats.visits_completed || 0} / {stats.visit_target || 20}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(((stats.visits_completed || 0) / (stats.visit_target || 20)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Collections Progress */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[12px] text-slate-600">Collection Target</span>
                    <span className="text-[12px] font-medium text-slate-700">
                      {formatCompactCurrency(stats.total_collections)} / {formatCompactCurrency(stats.collection_target || 50000)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(((stats.total_collections || 0) / (stats.collection_target || 50000)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[14px] font-semibold text-slate-700">Quick Stats</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="text-[11px] text-slate-500 uppercase tracking-wider">Rating</span>
                    </div>
                    <p className="text-[20px] font-semibold text-slate-800">{stats.rating || 0}/5</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1.5">
                      <BarChart3 className="w-4 h-4 text-teal-500" />
                      <span className="text-[11px] text-slate-500 uppercase tracking-wider">Rank</span>
                    </div>
                    <p className="text-[20px] font-semibold text-slate-800">#{stats.rank || '---'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Target className="w-4 h-4 text-purple-500" />
                      <span className="text-[11px] text-slate-500 uppercase tracking-wider">Achievement</span>
                    </div>
                    <p className="text-[20px] font-semibold text-slate-800">{stats.achievement || 0}%</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <span className="text-[11px] text-slate-500 uppercase tracking-wider">Avg Time</span>
                    </div>
                    <p className="text-[20px] font-semibold text-slate-800">{stats.avg_visit_time || 0}m</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPerformancePage;
